/**
 * Alerts API client — CRUD for alert_configs.
 */

import { auth } from '../firebase';
import type { AlertConfig, AlertConfigCreate, AlertConfigUpdate } from '@/types/analytics';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export async function fetchAlerts(): Promise<AlertConfig[]> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/analytics/alerts`, { headers });
  if (!res.ok) throw new Error(`Alerts API error: ${res.status}`);
  const data = await res.json();
  return data.alerts;
}

export async function createAlert(body: AlertConfigCreate): Promise<AlertConfig> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/analytics/alerts`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Create alert error: ${res.status}`);
  return res.json();
}

export async function updateAlert(id: string, body: AlertConfigUpdate): Promise<AlertConfig> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/analytics/alerts/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Update alert error: ${res.status}`);
  return res.json();
}

export async function deleteAlert(id: string): Promise<void> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/analytics/alerts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error(`Delete alert error: ${res.status}`);
}
